"use client";

import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Base immuable du bouton d'aperçu. Les classes utilitaires choisies viennent
// s'y ajouter telles quelles (jamais de concaténation de fragments).
const PREVIEW_BASE =
    "inline-flex items-center justify-center text-white select-none transition-all duration-300";

// IMPORTANT (Tailwind v4) : toute classe utilisée au runtime doit apparaître
// VERBATIM ici en tant que chaîne littérale, sinon elle n'est pas compilée.
const EXCLUSIVE_GROUPS = [
    {
        id: "rounded",
        labelKey: "Tailwind_composer_group_rounded",
        options: ["rounded-none", "rounded-lg", "rounded-2xl", "rounded-full"],
    },
    {
        id: "shadow",
        labelKey: "Tailwind_composer_group_shadow",
        options: ["shadow-none", "shadow-md", "shadow-xl"],
    },
    {
        id: "bg",
        labelKey: "Tailwind_composer_group_bg",
        options: ["bg-sky-500", "bg-emerald-500", "bg-violet-500", "bg-rose-500"],
    },
    {
        id: "spacing",
        labelKey: "Tailwind_composer_group_spacing",
        options: ["px-4 py-2", "px-6 py-3", "px-8 py-4"],
    },
];

const EFFECTS_GROUP = {
    id: "effects",
    labelKey: "Tailwind_composer_group_effects",
    options: ["font-bold", "italic", "ring-4 ring-sky-300", "scale-105", "tracking-wide"],
};

const DEFAULT_EXCLUSIVE = {
    rounded: "rounded-2xl",
    shadow: "shadow-xl",
    bg: "bg-sky-500",
    spacing: "px-6 py-3",
};

const DEFAULT_EFFECTS = ["font-bold"];

const CHIP_ACTIVE = "bg-sky-500/20 ring-1 ring-sky-400/50 text-white";
const CHIP_INACTIVE =
    "bg-white/5 ring-1 ring-white/10 text-gray-300 hover:bg-white/10";

export default function TailwindComposer() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const cardRef = useRef(null);
    const inView = useInView(cardRef, { once: true, margin: "-80px" });

    const [exclusive, setExclusive] = useState(DEFAULT_EXCLUSIVE);
    const [effects, setEffects] = useState(DEFAULT_EFFECTS);

    function selectExclusive(groupId, value) {
        setExclusive((prev) => ({ ...prev, [groupId]: value }));
    }

    function toggleEffect(value) {
        setEffects((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value],
        );
    }

    function reset() {
        setExclusive(DEFAULT_EXCLUSIVE);
        setEffects(DEFAULT_EFFECTS);
    }

    // Composition de la classe finale : on ne joint que des chaînes littérales
    // déjà présentes dans la config (ordre : groupes exclusifs puis effets).
    const activeUtilities = [
        ...EXCLUSIVE_GROUPS.map((group) => exclusive[group.id]),
        ...EFFECTS_GROUP.options.filter((opt) => effects.includes(opt)),
    ].filter(Boolean);

    const previewClassName = [PREVIEW_BASE, ...activeUtilities].join(" ");
    const composedString = activeUtilities.join(" ");

    return (
        <section id="tailwind-composer" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Tailwind_composer_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Tailwind_composer_intro")}
            </p>

            <div
                ref={cardRef}
                className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10"
            >
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:tailwindcss-icon"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        design.html
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Tailwind_composer_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative grid lg:grid-cols-2">
                    {/* APERÇU LIVE */}
                    <div className="p-6 sm:p-10 bg-black/20 flex flex-col items-center justify-center gap-7 border-b lg:border-b-0 lg:border-r border-white/10">
                        <motion.div
                            initial={reduce ? false : { opacity: 0, scale: 0.94 }}
                            animate={
                                reduce
                                    ? undefined
                                    : inView
                                      ? { opacity: 1, scale: 1 }
                                      : { opacity: 0, scale: 0.94 }
                            }
                            transition={{ duration: 0.45, ease: "easeOut" }}
                            className="flex items-center justify-center"
                        >
                            <span className={previewClassName}>
                                {t("Tailwind_composer_preview_text")}
                            </span>
                        </motion.div>

                        {/* Chaîne de classe générée */}
                        <div className="w-full max-w-[360px]">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-2">
                                {t("Tailwind_composer_class_label")}
                            </div>
                            <div className="rounded-xl bg-black/30 ring-1 ring-white/10 px-3.5 py-3 font-mono text-[12.5px] leading-relaxed break-words">
                                <span className="text-gray-400">{"<span "}</span>
                                <span className="text-gray-500">class=</span>
                                <span className="text-gray-400">"</span>
                                <span className="text-sky-300 break-words">
                                    {composedString}
                                </span>
                                <span className="text-gray-400">"</span>
                                <span className="text-gray-400">{">"}</span>
                            </div>
                        </div>
                    </div>

                    {/* PANNEAU DE CLASSES */}
                    <div className="p-6 sm:p-8 flex flex-col gap-6">
                        {EXCLUSIVE_GROUPS.map((group) => (
                            <div key={group.id}>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 mb-2.5">
                                    {t(group.labelKey)}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {group.options.map((option) => {
                                        const isActive =
                                            exclusive[group.id] === option;
                                        return (
                                            <button
                                                key={option}
                                                type="button"
                                                aria-pressed={isActive}
                                                onClick={() =>
                                                    selectExclusive(
                                                        group.id,
                                                        option,
                                                    )
                                                }
                                                className={`rounded-lg px-3 py-1.5 text-[13px] font-mono transition active:scale-95 ${
                                                    isActive
                                                        ? CHIP_ACTIVE
                                                        : CHIP_INACTIVE
                                                }`}
                                            >
                                                {option}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        {/* Effets (multi-sélection) */}
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 mb-2.5">
                                {t(EFFECTS_GROUP.labelKey)}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {EFFECTS_GROUP.options.map((option) => {
                                    const isActive = effects.includes(option);
                                    return (
                                        <button
                                            key={option}
                                            type="button"
                                            aria-pressed={isActive}
                                            onClick={() => toggleEffect(option)}
                                            className={`rounded-lg px-3 py-1.5 text-[13px] font-mono transition active:scale-95 ${
                                                isActive
                                                    ? CHIP_ACTIVE
                                                    : CHIP_INACTIVE
                                            }`}
                                        >
                                            {option}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Réinitialiser */}
                        <div className="pt-1">
                            <button
                                type="button"
                                onClick={reset}
                                className="inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 ring-1 ring-white/10 px-3.5 py-2 text-[13px] font-semibold text-gray-200 transition active:scale-95"
                            >
                                <SolarIcon
                                    name="refresh-bold"
                                    className="w-4 h-4"
                                />
                                {t("Tailwind_composer_reset")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Tailwind_composer_caption")}
            </p>
        </section>
    );
}
